// One coordinator per advisor instance, never one size per response/rail.
// Measure the text children, not their stretched grid track, to avoid feedback.
type SizingScope = {cards: Set<HTMLElement>; observer?: ResizeObserver; frame: number; schedule: () => void};
const scopes = new WeakMap<HTMLElement, SizingScope>();
const MIN_CONTENT_HEIGHT = 64;

export function registerProductCardSizing(card: HTMLElement): () => void {
  const root = card.closest<HTMLElement>('.twe-drinks-advisor');
  if (!root) return () => undefined;
  let scope = scopes.get(root);
  if (!scope) {
    const created: SizingScope = {cards: new Set(), frame: 0, schedule: () => undefined};
    created.schedule = () => {
      if (created.frame) return;
      created.frame = requestAnimationFrame(() => {
        created.frame = 0;
        let height = MIN_CONTENT_HEIGHT;
        for (const node of created.cards) {
          const content = node.querySelector<HTMLElement>('.product-card__content');
          const first = content?.firstElementChild as HTMLElement | null;
          const last = content?.lastElementChild as HTMLElement | null;
          if (!content || !first || !last) continue;
          const style = getComputedStyle(content);
          const firstStyle = getComputedStyle(first);
          const lastStyle = getComputedStyle(last);
          const number = (value: string) => parseFloat(value) || 0;
          const natural = last.getBoundingClientRect().bottom - first.getBoundingClientRect().top
            + number(style.paddingTop) + number(style.paddingBottom)
            + number(firstStyle.marginTop) + number(lastStyle.marginBottom);
          height = Math.max(height, Math.ceil(natural));
        }
        const value = `${height}px`;
        if (root.style.getPropertyValue('--asa-product-content-height') !== value) {
          root.style.setProperty('--asa-product-content-height', value);
        }
      });
    };
    if (typeof ResizeObserver !== 'undefined') created.observer = new ResizeObserver(created.schedule);
    scopes.set(root, created);
    scope = created;
  }
  const active = scope;
  active.cards.add(card);
  const text = [...card.querySelectorAll<HTMLElement>('.product-card__name,.product-card__meta')];
  text.forEach(element => active.observer?.observe(element));
  active.schedule();
  // Covers late webfont loading even in browsers without ResizeObserver.
  void document.fonts?.ready.then(() => active.cards.size && active.schedule());
  return () => {
    text.forEach(element => active.observer?.unobserve(element));
    active.cards.delete(card);
    if (active.cards.size) active.schedule();
    else {
      active.observer?.disconnect();
      cancelAnimationFrame(active.frame);
      root.style.removeProperty('--asa-product-content-height');
      scopes.delete(root);
    }
  };
}
